import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { Org } from "../../schema/types/org/entity";
import { Gvc } from "../../schema/types/gvc/entity";
import { Workload } from "../../schema/types/workload/entity";
import { workloadToInput } from "./draft/utils/workloadToInput";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { inputToWorkload, workloadToPatch } from "./draft/utils/inputToWorkload";
import { WorkloadInput } from "../../schema/types/workload/input";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpPreviewModal } from "./draft/previewModal";
import { CpDebugModal } from "./draft/debugModal";
import { CpFormContext } from "../../components/form/CpFormContext";
import { WorkloadDetailContent } from "./detailContent";
import { getValidator } from "./draft/utils/validator";
import { DeploymentsReducerActionType, useDeploymentsReducer } from "../../components/detail/deploymentsReducer";
import { Deployment } from "../../schema/types/workload/deployment";
import { DEPLOYMENT_UPDATE_INTERVAL_MS } from "../../envVariables";
import { useGetWorkloadQuery } from "../../store/api/workloadApi";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { getDirtyChecker } from "./draft/utils/dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";
import { useDeploymentWebSocket } from "../../reactHooks/useDeploymentWebSocket";

interface Props {
  workload: Workload;
  org: Org;
  gvc?: Gvc;
  gvcName: string;
}
export const WorkloadDetailForm: React.FC<Props> = ({ workload, org, gvc, gvcName }) => {
  const selfLink = linksOf(workload).self!;

  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetWorkloadQuery(linksOf(workload).self!);

  // deployment handling
  const { state, fetchDeployments, dispatch: deploymentDispatch } = useDeploymentsReducer();

  React.useEffect(() => {
    let dispose: (() => void) | undefined;
    if (workload) {
      dispose = fetchDeployments([linksOf(workload).self!], DEPLOYMENT_UPDATE_INTERVAL_MS, () => {
        refetch();
      });
    }
    return () => {
      if (dispose) {
        dispose();
      }
    };
  }, [workload]);

  const handleDeploymentUpdate = React.useCallback(
    (deployment: Deployment) => {
      // Verify deployment belongs to current workload (prevents race condition during navigation)
      const deploymentWorkloadLink = linksOf(deployment).workload;
      if (deploymentWorkloadLink !== linksOf(workload).self) {
        return; // Ignore deployments for other workloads
      }

      deploymentDispatch({
        type: DeploymentsReducerActionType.UPDATE_DEPLOYMENT,
        payload: { link: linksOf(workload).self!, deployments: [deployment] },
      });
    },
    [workload, deploymentDispatch],
  );

  useDeploymentWebSocket({
    org: org.name,
    gvc: gvcName,
    workload: workload.name,
    onMessage: handleDeploymentUpdate,
    enabled: !!workload,
  });

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = workloadToInput(workload);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: workloadToInput(workload) }));
  }, [workload.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId, selfLink));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: WorkloadInput) {
    try {
      let body = workloadToPatch(inputToWorkload(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(workload).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Workload is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <WorkloadDetailContent
        org={org}
        gvc={gvc}
        gvcName={gvcName}
        workload={workload}
        deployments={state.deploymentsMap[linksOf(workload).self!] || []}
        isDeploymentsLoaded={state.requestedLinks.includes(linksOf(workload).self!)}
        refetch={refetch}
      />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(workload).self!} inputToItemFn={inputToWorkload} />
      <CpPreviewModal inputToItemFn={inputToWorkload} kind="workload" namePath="name" title="Workload" />
      <CpDebugModal inputToItemFn={inputToWorkload} kind="workload" namePath="name" title="Workload" />
    </CpFormContext.Provider>
  );
};
